import { useState, useEffect, useRef, useCallback } from "react";
import { Bot, Send, Sparkles, X, Loader2, MessageSquare, Plus } from "lucide-react";
import { Button } from "../ui/button";
import { toast } from "sonner";

/**
 * Sidebar conversationnel avec l'agent IA Gemini 3 Pro.
 * - Reprend la session active (localStorage) ou en cree une nouvelle
 * - Pre-fill un message si context.action est fourni
 * - Affiche l'historique de la session courante
 */
export default function GoogleAgentSidebar({ api, context, onClose }) {
  const [sessionId, setSessionId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const messagesEndRef = useRef(null);

  const loadSession = useCallback(async (sid) => {
    setLoadingHistory(true);
    try {
      const r = await api.get(`/google/agent/sessions/${sid}/messages`);
      setMessages(r.data || []);
    } catch (e) {
      setMessages([]);
    }
    setLoadingHistory(false);
  }, [api]);

  // Init session — reprendre celle stockee ou en commencer une nouvelle
  useEffect(() => {
    const stored = localStorage.getItem("blitzz_google_agent_session");
    if (stored) {
      setSessionId(stored);
      loadSession(stored);
    }
  }, [loadSession]);

  // Pre-fill input si action contextualisee
  useEffect(() => {
    if (context?.action) {
      const presets = {
        audit_seo: `Analyse cet audit SEO et donne-moi 3-5 actions concretes par ordre de priorite.`,
        reply_review: `Redige une reponse a cet avis Google.`,
        create_ads_campaign: `Propose une campagne Google Ads complete pour ${context.account || "ce business"}.`,
      };
      setInput(presets[context.action] || "");
    }
  }, [context?.action, context?.account]);

  const sendMessage = async () => {
    if (!input.trim() || sending) return;
    const userText = input.trim();
    setSending(true);
    setInput("");
    // Optimistic UI : afficher le message user immediatement
    const tempUserMsg = {
      message_id: `tmp-${Date.now()}`,
      role: "user",
      text: userText,
      created_at: new Date().toISOString(),
    };
    setMessages(prev => [...prev, tempUserMsg]);

    try {
      const r = await api.post("/google/agent/chat", {
        text: userText,
        context: context && Object.keys(context).length ? context : undefined,
        session_id: sessionId,
      });
      const newSid = r.data.session_id;
      if (newSid && newSid !== sessionId) {
        setSessionId(newSid);
        localStorage.setItem("blitzz_google_agent_session", newSid);
      }
      // Recharger la session complete pour avoir les vrais IDs
      if (newSid) await loadSession(newSid);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur agent");
      // Retirer le message optimistic
      setMessages(prev => prev.filter(m => m.message_id !== tempUserMsg.message_id));
    }
    setSending(false);
  };

  const newConversation = () => {
    setSessionId(null);
    localStorage.removeItem("blitzz_google_agent_session");
    setMessages([]);
    setInput("");
  };

  // Scroll to bottom on new message
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  return (
    <div className="bg-[#14101c] border border-white/5 rounded-xl flex flex-col h-[calc(100vh-180px)] sticky top-4" data-testid="google-agent-sidebar">
      {/* Header */}
      <div className="px-4 py-3 border-b border-white/5 flex items-center justify-between bg-gradient-to-r from-[#4285F4]/10 to-[#EA4335]/10">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-[#4285F4] to-[#EA4335] flex items-center justify-center">
            <Bot className="w-4 h-4 text-white" />
          </div>
          <div>
            <div className="text-sm font-bold text-white">Agent Marketing Google</div>
            <div className="text-[10px] text-slate-400">Gemini 2.5 Pro</div>
          </div>
        </div>
        <div className="flex items-center gap-1">
          <button onClick={newConversation} className="p-1.5 rounded text-slate-400 hover:text-white hover:bg-white/5" title="Nouvelle conversation" data-testid="agent-new-conv-btn">
            <Plus className="w-4 h-4" />
          </button>
          <button onClick={onClose} className="p-1.5 rounded text-slate-400 hover:text-white hover:bg-white/5" data-testid="agent-close-btn">
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 space-y-3" data-testid="agent-messages-container">
        {loadingHistory ? (
          <div className="text-center py-8 text-xs text-slate-500"><Loader2 className="w-4 h-4 animate-spin mx-auto mb-2" /> Chargement...</div>
        ) : messages.length === 0 ? (
          <div className="text-center py-8">
            <Sparkles className="w-8 h-8 text-[#f7c935] mx-auto mb-2" />
            <div className="text-xs font-bold text-white mb-1">Demarrer une conversation</div>
            <p className="text-[10px] text-slate-500 leading-relaxed px-3">L'agent IA peut auditer un site, repondre a des avis, drafter des campagnes Ads et plus.</p>
            <div className="mt-4 space-y-1.5 text-left">
              {[
                "Audite blitzz.fr et donne-moi un score SEO",
                "Comment repondre a un avis 1 etoile ?",
                "Cree une campagne Ads pour un cabinet de conseil",
              ].map((s, i) => (
                <button key={i} onClick={() => setInput(s)} className="block w-full text-[10px] text-left px-2 py-1.5 rounded bg-white/5 text-slate-300 hover:bg-white/10" data-testid={`agent-suggest-${i}`}>
                  {s}
                </button>
              ))}
            </div>
          </div>
        ) : (
          messages.map(m => (
            <div key={m.message_id} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`} data-testid={`agent-msg-${m.role}`}>
              <div className={`max-w-[85%] rounded-xl px-3 py-2 text-xs leading-relaxed whitespace-pre-wrap ${m.role === "user" ? "bg-[#f7c935] text-[#0b0811] font-medium" : m.error ? "bg-red-500/10 text-red-300 border border-red-500/20" : "bg-[#0b0811] text-slate-200 border border-white/5"}`}>
                {m.text}
              </div>
            </div>
          ))
        )}
        {sending && (
          <div className="flex justify-start">
            <div className="bg-[#0b0811] border border-white/5 rounded-xl px-3 py-2 text-xs text-slate-400 flex items-center gap-2">
              <Loader2 className="w-3 h-3 animate-spin" /> Reflexion...
            </div>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <div className="p-3 border-t border-white/5">
        {context?.url && (
          <div className="text-[10px] text-slate-500 mb-2 flex items-center gap-1">
            <MessageSquare className="w-3 h-3" /> Contexte : {context.url}
          </div>
        )}
        <div className="flex items-end gap-2">
          <textarea
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={e => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                sendMessage();
              }
            }}
            placeholder="Votre message..."
            rows={2}
            className="flex-1 bg-[#0b0811] border border-white/10 rounded-lg px-3 py-2 text-xs text-white resize-none focus:border-[#f7c935] outline-none"
            data-testid="agent-input"
          />
          <Button onClick={sendMessage} disabled={!input.trim() || sending} className="bg-gradient-to-r from-[#4285F4] to-[#EA4335] hover:opacity-90 text-white h-9 w-9 p-0 flex-shrink-0" data-testid="agent-send-btn">
            {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
          </Button>
        </div>
      </div>
    </div>
  );
}
